import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
  ComposableMap,
  Geographies,
  Geography,
  Sphere,
  Graticule,
  ZoomableGroup,
  Marker
} from 'react-simple-maps';
import { feature } from 'topojson-client';
import { geoCentroid } from 'd3-geo';
import { animate } from 'framer-motion';
import { getCountryISO, getCountryColor, getCountryColorDark } from '../utils/countryMapping';

// Predefined coordinates for small countries that might not be well-represented in TopoJSON
const COUNTRY_COORDINATES = {
  'Bahrain': [50.5577, 26.0667],
  'Qatar': [51.1839, 25.3548],
  'Kuwait': [47.4818, 29.3117],
  'United Arab Emirates': [53.8478, 23.4241],
  'Singapore': [103.8198, 1.3521],
  'Malta': [14.3754, 35.9375],
  'Luxembourg': [6.1296, 49.8153],
  'Monaco': [7.4167, 43.7333],
  'San Marino': [12.4578, 43.9424],
  'Vatican City': [12.4534, 41.9033],
  'Liechtenstein': [9.5215, 47.1410],
  'Andorra': [1.5218, 42.5063],
  'United States': [-95.7129, 37.0902],
  'USA': [-95.7129, 37.0902],
  'US': [-95.7129, 37.0902],
  'Turkey': [35.2433, 38.9637],
  'Türkiye': [35.2433, 38.9637],
  'The Netherlands': [5.2913, 52.1326],
  'Netherlands': [5.2913, 52.1326]
};

// Use local TopoJSON file to avoid CORS/CSP issues
const geoUrls = [
  "/Assets/countries-110m.json" // Local file hosted in public/Assets/
];

const getCurrentGeoUrl = () => geoUrls[0];

const WorldMap = ({ countriesData = [], isDarkMode = false, className = "", selectedCountry = null }) => {
  const [tooltipContent, setTooltipContent] = useState("");
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [showTooltip, setShowTooltip] = useState(false);
  const [mapError, setMapError] = useState(false);
  const [mapLoading, setMapLoading] = useState(true);
  const [currentGeoUrl] = useState(getCurrentGeoUrl());
  const [geoFeatures, setGeoFeatures] = useState([]);
  const [zoom, setZoom] = useState(1);
  const [center, setCenter] = useState([0, 0]);
  const animationRef = useRef(null);
  const currentCenterRef = useRef([0, 0]);
  const currentZoomRef = useRef(1);

  // Update refs when state changes
  useEffect(() => {
    currentCenterRef.current = center;
    currentZoomRef.current = zoom;
  }, [center, zoom]);

  // Handle selectedCountry prop to zoom to specific country with smooth animation
  useEffect(() => {
    if (selectedCountry && geoFeatures.length > 0) {
      // Cancel any ongoing animation
      if (animationRef.current) {
        animationRef.current.stop();
      }

      // Find the country in the TopoJSON data
       const country = geoFeatures.find(d => {
         const countryLower = selectedCountry.toLowerCase();
         const props = d.properties;
         
         // Handle Turkey/Türkiye special case
         if (countryLower === 'türkiye' || countryLower === 'turkey') {
           return props.name?.toLowerCase() === 'turkey' ||
                  props.NAME?.toLowerCase() === 'turkey' ||
                  props.NAME_EN?.toLowerCase() === 'turkey' ||
                  props.ADMIN?.toLowerCase() === 'turkey' ||
                  props.name?.toLowerCase() === 'türkiye' ||
                  props.NAME?.toLowerCase() === 'türkiye' ||
                  props.NAME_EN?.toLowerCase() === 'türkiye' ||
                  props.ADMIN?.toLowerCase() === 'türkiye';
         }
         
         return props.name?.toLowerCase() === countryLower ||
                props.NAME?.toLowerCase() === countryLower ||
                props.NAME_EN?.toLowerCase() === countryLower ||
                props.ADMIN?.toLowerCase() === countryLower;
       });

       let targetCenter, targetZoom;
       
       if (country) {
         // Calculate the centroid of the country
         targetCenter = geoCentroid(country);
         targetZoom = 4;
       } else {
         // Fallback to predefined coordinates for small countries
         const fallbackCoords = COUNTRY_COORDINATES[selectedCountry] || 
                               Object.entries(COUNTRY_COORDINATES).find(([key]) => 
                                 key.toLowerCase() === selectedCountry.toLowerCase()
                               )?.[1];
         if (fallbackCoords) {
             targetCenter = fallbackCoords;
             targetZoom = 8; // Higher zoom for small countries
           } else {
             return; // Country not found
           }
       }

      // Animate to the target position
      const startCenter = currentCenterRef.current;
      const startZoom = currentZoomRef.current;
      
      animationRef.current = animate(0, 1, {
        duration: 1.2,
        ease: "easeInOut",
        onUpdate: (progress) => {
          // Interpolate center coordinates
          const newCenter = [
            startCenter[0] + (targetCenter[0] - startCenter[0]) * progress,
            startCenter[1] + (targetCenter[1] - startCenter[1]) * progress
          ];
          
          // Interpolate zoom
          const newZoom = startZoom + (targetZoom - startZoom) * progress;
          
          setCenter(newCenter);
          setZoom(newZoom);
        }
      });
    } else if (!selectedCountry) {
      // Cancel any ongoing animation
      if (animationRef.current) {
        animationRef.current.stop();
      }
      
      // Animate back to world view when no country is selected
      const startCenter = currentCenterRef.current;
      const startZoom = currentZoomRef.current;
      
      animationRef.current = animate(0, 1, {
        duration: 1.0,
        ease: "easeInOut",
        onUpdate: (progress) => {
          // Interpolate center coordinates back to [0, 0]
          const newCenter = [
            startCenter[0] + (0 - startCenter[0]) * progress,
            startCenter[1] + (0 - startCenter[1]) * progress
          ];
          
          // Interpolate zoom back to 1
          const newZoom = startZoom + (1 - startZoom) * progress;
          
          setCenter(newCenter);
          setZoom(newZoom);
        }
      });
    }
  }, [selectedCountry, geoFeatures]);

   // Cleanup animation on unmount
   useEffect(() => {
     return () => {
       if (animationRef.current) {
         animationRef.current.stop();
       }
     };
   }, []);

  const handleRetry = () => {
    setMapLoading(true);
    setMapError(false);
    // Force re-render by updating a key or state
    window.location.reload();
  };

  // Load and parse TopoJSON data
  useEffect(() => {
    fetch(currentGeoUrl)
      .then((response) => response.json())
      .then((topology) => {
        const featuresData = feature(topology, topology.objects.countries).features;
        setGeoFeatures(featuresData);
        setMapLoading(false);
        setMapError(false);
        // Successfully loaded and parsed TopoJSON data
      })
      .catch((error) => {
        console.error('Failed to load and parse TopoJSON:', error);
        setMapError(true);
        setMapLoading(false);
      });
  }, [currentGeoUrl]);

  // Process countries data for choropleth mapping
  const processedCountriesData = useMemo(() => {
    const countryMap = new Map();
    let maxCount = 0;

    countriesData.forEach(country => {
      // Handle both API format (country, unique_visitors, total_visits) and processed format (name, count)
      const countryName = country.country || country.name;
      const visitCount = country.unique_visitors || country.count || 0;
      
      const iso = getCountryISO(countryName);
      if (iso && countryName) {
        countryMap.set(iso, {
          name: countryName,
          count: visitCount
        });
        maxCount = Math.max(maxCount, visitCount);
      }
    });

    return { countryMap, maxCount };
  }, [countriesData]);

  const handleMouseEnter = (geo, event) => {
    const { name } = geo.properties;
    const countryName = name || 'Unknown';
    
    // Try to get ISO code from country name
    const iso = getCountryISO(countryName);
    const countryData = iso ? processedCountriesData.countryMap.get(iso) : null;
    
    if (countryData) {
      setTooltipContent(`${countryName}: ${countryData.count} visits`);
    } else {
      setTooltipContent(`${countryName}: 0 visits`);
    }
    
    // Get the map container's bounding rect for proper positioning
    const mapContainer = event.currentTarget.closest('div');
    const rect = mapContainer ? mapContainer.getBoundingClientRect() : { left: 0, top: 0 };
    
    setTooltipPosition({ 
      x: event.clientX - rect.left, 
      y: event.clientY - rect.top 
    });
    setShowTooltip(true);
  };

  const handleMouseLeave = () => {
    setShowTooltip(false);
  };

  const handleMouseMove = (event) => {
    // Get the map container's bounding rect for proper positioning
    const mapContainer = event.currentTarget.closest('div');
    const rect = mapContainer ? mapContainer.getBoundingClientRect() : { left: 0, top: 0 };
    
    setTooltipPosition({ 
      x: event.clientX - rect.left, 
      y: event.clientY - rect.top 
    });
  };

  const handleZoomIn = () => {
    setZoom((prevZoom) => Math.min(prevZoom + 1, 10));
  };

  const handleZoomOut = () => {
    setZoom((prevZoom) => Math.max(prevZoom - 1, 1));
  };

  const handleReset = () => {
    setZoom(1);
    setCenter([0, 0]);
  };

  const handleGeographyClick = (geo) => {
    // Try to get centroid from properties first, then calculate it, then use fallback coordinates
    let centroid = geo.properties.centroid;
    
    if (!centroid) {
      try {
        // Calculate centroid using d3-geo
        centroid = geoCentroid(geo);
      } catch (error) {
        console.warn('Failed to calculate centroid for country:', geo.properties.name || geo.properties.NAME, error);
        centroid = null;
      }
    }
    
    // If still no centroid, try to find it in COUNTRY_COORDINATES
    if (!centroid || (Array.isArray(centroid) && (isNaN(centroid[0]) || isNaN(centroid[1])))) {
      const countryName = geo.properties.name || geo.properties.NAME || geo.properties.NAME_EN || geo.properties.ADMIN;
      centroid = COUNTRY_COORDINATES[countryName] || [0, 0];
    }
    
    setCenter(centroid);
    setZoom(4);
  };

  return (
    <div className={`relative ${className}`}>
      <ComposableMap
        projection="geoNaturalEarth1"
        projectionConfig={{
          scale: 140,
          center: [0, 0]
        }}
        width={800}
        height={400}
        style={{
          width: "100%",
          height: "auto"
        }}
      >
        <ZoomableGroup
          zoom={zoom}
          center={center}
          minZoom={1}
          maxZoom={10}
        >
          {/* Background sphere */}
          <Sphere
            stroke={isDarkMode ? "#374151" : "#e5e7eb"}
            strokeWidth={0.5}
            fill={isDarkMode ? "#1f2937" : "#f9fafb"}
          />
          
          {/* Graticule (grid lines) */}
          <Graticule
            stroke={isDarkMode ? "#4b5563" : "#d1d5db"}
            strokeWidth={0.3}
            strokeOpacity={0.5}
          />
        
        {/* Countries */}
        {geoFeatures.length > 0 && (
          <Geographies geography={geoFeatures}>
            {({ geographies }) => {
              // Create a set of countries that are visible in geography for marker filtering
              const visibleCountries = new Set();
              geographies.forEach((geo) => {
                if (geo && geo.properties) {
                  const { name } = geo.properties;
                  const iso = getCountryISO(name);
                  if (iso) {
                    visibleCountries.add(iso);
                    // Also add common name variations
                    visibleCountries.add(name?.toLowerCase());
                    visibleCountries.add(geo.properties.NAME?.toLowerCase());
                    visibleCountries.add(geo.properties.NAME_EN?.toLowerCase());
                    visibleCountries.add(geo.properties.ADMIN?.toLowerCase());
                  }
                }
              });
              
              return geographies.map((geo) => {
                // Additional safety check for geo properties
                if (!geo || !geo.properties) {
                  return null;
                }
                const { name } = geo.properties;
                const iso = getCountryISO(name);
                const countryData = iso ? processedCountriesData.countryMap.get(iso) : null;
                const count = countryData?.count || 0;
                
                // Check if this is the selected country
                const isSelected = selectedCountry && (() => {
                  const selectedLower = selectedCountry.toLowerCase();
                  const props = geo.properties;
                  
                  // Handle Turkey/Türkiye special case
                  if (selectedLower === 'türkiye' || selectedLower === 'turkey') {
                    return name?.toLowerCase() === 'turkey' ||
                           props.NAME?.toLowerCase() === 'turkey' ||
                           props.NAME_EN?.toLowerCase() === 'turkey' ||
                           props.ADMIN?.toLowerCase() === 'turkey' ||
                           name?.toLowerCase() === 'türkiye' ||
                           props.NAME?.toLowerCase() === 'türkiye' ||
                           props.NAME_EN?.toLowerCase() === 'türkiye' ||
                           props.ADMIN?.toLowerCase() === 'türkiye';
                  }
                  
                  return name?.toLowerCase() === selectedLower ||
                         props.NAME?.toLowerCase() === selectedLower ||
                         props.NAME_EN?.toLowerCase() === selectedLower ||
                         props.ADMIN?.toLowerCase() === selectedLower;
                })();
                
                let fillColor;
                 if (isSelected) {
                   // Use a distinct green color for the selected country
                   fillColor = isDarkMode ? "#10b981" : "#059669"; // Green color for selection
                 } else {
                   fillColor = isDarkMode 
                     ? getCountryColorDark(count, processedCountriesData.maxCount)
                     : getCountryColor(count, processedCountriesData.maxCount);
                 }

                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    fill={fillColor}
                    stroke={isDarkMode ? "#374151" : "#e5e7eb"}
                    strokeWidth={0.5}
                    style={{
                      default: {
                        outline: "none"
                      },
                      hover: {
                        fill: isDarkMode ? "#3b82f6" : "#2563eb",
                        outline: "none",
                        cursor: "pointer"
                      },
                      pressed: {
                        fill: isDarkMode ? "#1e40af" : "#1d4ed8",
                        outline: "none"
                      }
                    }}
                    onMouseEnter={(event) => handleMouseEnter(geo, event)}
                    onMouseLeave={handleMouseLeave}
                    onMouseMove={handleMouseMove}
                    onClick={() => handleGeographyClick(geo)}
                  />
                );
              });
            }}
          </Geographies>
        )}
          
          {/* Render markers for small countries */}
          {!mapLoading && geoFeatures && (() => {
            // Create visibleCountries set from geography data
            const visibleCountries = new Set();
            if (geoFeatures.length > 0) {
              geoFeatures.forEach((geo) => {
                if (geo && geo.properties) {
                  const { name } = geo.properties;
                  const iso = getCountryISO(name);
                  if (iso) {
                    visibleCountries.add(iso);
                    // Also add common name variations
                    visibleCountries.add(name?.toLowerCase());
                    visibleCountries.add(geo.properties.NAME?.toLowerCase());
                    visibleCountries.add(geo.properties.NAME_EN?.toLowerCase());
                    visibleCountries.add(geo.properties.ADMIN?.toLowerCase());
                  }
                }
              });
            }
            
            return Object.entries(COUNTRY_COORDINATES).map(([countryName, coordinates]) => {
              const iso = getCountryISO(countryName);
              const countryData = iso ? processedCountriesData.countryMap.get(iso) : null;
              const count = countryData?.count || 0;
              
              // Only render marker if country has visitor data
              if (count === 0) return null;
              
              // Don't render marker if country is already visible in geography
              if (iso && visibleCountries.has(iso)) return null;
              if (visibleCountries.has(countryName?.toLowerCase())) return null;
            
            // Check if this is the selected country
            const isSelected = selectedCountry && (
              countryName?.toLowerCase() === selectedCountry.toLowerCase()
            );
            
            let fillColor;
            if (isSelected) {
              // Use green color for selected country
              fillColor = isDarkMode ? "#10b981" : "#059669";
            } else {
              fillColor = isDarkMode 
                ? getCountryColorDark(count, processedCountriesData.maxCount)
                : getCountryColor(count, processedCountriesData.maxCount);
            }
            
            return (
                <Marker key={countryName} coordinates={coordinates}>
                  <circle
                    r={2}
                    fill={fillColor}
                  stroke={isDarkMode ? "#374151" : "#e5e7eb"}
                  strokeWidth={1}
                  style={{
                    cursor: "pointer"
                  }}
                  onMouseEnter={(event) => {
                    setTooltipContent(`${countryName}: ${count} visit${count !== 1 ? 's' : ''}`);
                    
                    // Get the map container's bounding rect for proper positioning
                    const mapContainer = event.currentTarget.closest('div');
                    const rect = mapContainer ? mapContainer.getBoundingClientRect() : { left: 0, top: 0 };
                    
                    setTooltipPosition({ 
                      x: event.clientX - rect.left, 
                      y: event.clientY - rect.top 
                    });
                    setShowTooltip(true);
                  }}
                  onMouseLeave={() => {
                    setShowTooltip(false);
                  }}
                  onMouseMove={(event) => {
                    // Get the map container's bounding rect for proper positioning
                    const mapContainer = event.currentTarget.closest('div');
                    const rect = mapContainer ? mapContainer.getBoundingClientRect() : { left: 0, top: 0 };
                    
                    setTooltipPosition({ 
                      x: event.clientX - rect.left, 
                      y: event.clientY - rect.top 
                    });
                  }}
                  onClick={() => {
                    // Handle marker click - could trigger country selection
                    console.log(`Clicked on ${countryName}`);
                  }}
                />
              </Marker>
            );
          });
        })()}
        </ZoomableGroup>
      </ComposableMap>

      {/* Loading State */}
      {mapLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100 dark:bg-gray-800 rounded-lg">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
            <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Loading map...</p>
          </div>
        </div>
      )}

      {/* Error State */}
       {mapError && (
         <div className="absolute inset-0 flex items-center justify-center bg-gray-100 dark:bg-gray-800 rounded-lg">
           <div className="text-center p-4">
             <div className={`text-lg mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>🗺️</div>
             <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Unable to load world map</p>
             <p className={`text-xs mt-1 mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Please check your internet connection</p>
             <button
               onClick={handleRetry}
               className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
             >
               Retry
             </button>
           </div>
         </div>
       )}

      {/* Zoom Controls */}
      <div className="absolute top-4 right-4 flex flex-col space-y-2 z-40">
        <button
          onClick={handleZoomIn}
          disabled={zoom >= 10}
          className={`w-10 h-10 rounded-lg shadow-lg flex items-center justify-center text-lg font-bold transition-all ${
            isDarkMode 
              ? 'bg-gray-800 text-white border border-gray-600 hover:bg-gray-700 disabled:bg-gray-900 disabled:text-gray-600' 
              : 'bg-white text-gray-900 border border-gray-200 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400'
          } disabled:cursor-not-allowed`}
          title="Zoom In"
        >
          +
        </button>
        <button
          onClick={handleZoomOut}
          disabled={zoom <= 1}
          className={`w-10 h-10 rounded-lg shadow-lg flex items-center justify-center text-lg font-bold transition-all ${
            isDarkMode 
              ? 'bg-gray-800 text-white border border-gray-600 hover:bg-gray-700 disabled:bg-gray-900 disabled:text-gray-600' 
              : 'bg-white text-gray-900 border border-gray-200 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400'
          } disabled:cursor-not-allowed`}
          title="Zoom Out"
        >
          −
        </button>
        <button
          onClick={handleReset}
          className={`w-10 h-10 rounded-lg shadow-lg flex items-center justify-center text-xs font-medium transition-all ${
            isDarkMode 
              ? 'bg-gray-800 text-white border border-gray-600 hover:bg-gray-700' 
              : 'bg-white text-gray-900 border border-gray-200 hover:bg-gray-50'
          }`}
          title="Reset View"
        >
          ⌂
        </button>
      </div>

      {/* Tooltip */}
      {showTooltip && (
        <div
          className={`absolute z-50 px-3 py-2 text-sm rounded-lg shadow-lg pointer-events-none ${
            isDarkMode 
              ? 'bg-gray-800 text-white border border-gray-600' 
              : 'bg-white text-gray-900 border border-gray-200'
          }`}
          style={{
            left: tooltipPosition.x + 10,
            top: tooltipPosition.y - 35,
            transform: 'none'
          }}
        >
          {tooltipContent}
        </div>
      )}

      {/* Legend */}
      <div className={`absolute bottom-4 left-4 p-3 rounded-lg shadow-lg ${
        isDarkMode ? 'bg-gray-800 border border-gray-600' : 'bg-white border border-gray-200'
      }`}>
        <div className={`text-sm font-medium mb-2 ${
          isDarkMode ? 'text-white' : 'text-gray-900'
        }`}>
          Visitor Count
        </div>
        <div className="flex items-center space-x-2">
          <div className="flex flex-col space-y-1">
            <div className="flex items-center space-x-2">
              <div 
                className="w-4 h-3 rounded-sm" 
                style={{ backgroundColor: isDarkMode ? getCountryColorDark(0, 1) : getCountryColor(0, 1) }}
              />
              <span className={`text-xs ${
                isDarkMode ? 'text-gray-300' : 'text-gray-600'
              }`}>0</span>
            </div>
            <div className="flex items-center space-x-2">
              <div 
                className="w-4 h-3 rounded-sm" 
                style={{ backgroundColor: isDarkMode ? getCountryColorDark(0.2, 1) : getCountryColor(0.2, 1) }}
              />
              <span className={`text-xs ${
                isDarkMode ? 'text-gray-300' : 'text-gray-600'
              }`}>Low</span>
            </div>
            <div className="flex items-center space-x-2">
              <div 
                className="w-4 h-3 rounded-sm" 
                style={{ backgroundColor: isDarkMode ? getCountryColorDark(0.6, 1) : getCountryColor(0.6, 1) }}
              />
              <span className={`text-xs ${
                isDarkMode ? 'text-gray-300' : 'text-gray-600'
              }`}>Medium</span>
            </div>
            <div className="flex items-center space-x-2">
              <div 
                className="w-4 h-3 rounded-sm" 
                style={{ backgroundColor: isDarkMode ? getCountryColorDark(1, 1) : getCountryColor(1, 1) }}
              />
              <span className={`text-xs ${
                isDarkMode ? 'text-gray-300' : 'text-gray-600'
              }`}>High</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorldMap;